import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Form,
    Divider,
    notification,
} from 'antd';

import CustomTextArea from '@/components/TextArea';
import HeaderWithHelp from '@/components/HeaderWithHelp';
import DashedContainer from '@/components/DashedContainer';
import TourExtraServices from '@/components/TourExtraServices';
import Loading from '@/components/Loading';
import WidgetCategory from '@/components/WidgetCategory';
import useTranslatedValidationConfig from '@/hooks/useTranslatedValidationConfig';
import useApiValidationsForForm from '@/hooks/useApiValidationsForForm';
import { extractUrlFromFile } from '@/helpers/upload';
import { returnDeletedImages } from '@/helpers/deleteImages';
import { formatExtraServicesForSubmit, eServicesFormat } from '@/helpers/mapping';
import { selectors } from '@/redux/tour/packages/create/model';
import { actions } from '@/redux/tour/packages/reducer';

import ImagesUpload from './ImagesUpload';
import PackageDetails from './PackageDetails';
import PackagePrices from './PackagePrices';
import baseValidations from './validations';

const formFields = [
    'images',
    'name',
    'duration',
    'duration_type',
    'description',
    'tour_types',
    'adults',
    'adult_price',
    'adult_price_type',
    'children',
    'child_price',
    'infants',
    'infant_price',
];

const question = 'What is this?';
const answer = 'Lorem ipsum dolor sit amet...';

// format package type as initial data
const packageTypeFormat = (types) => types.map((type) => (
    type.id
));

const formatDataToSubmit = (id, categoryId, values, initialImages) => ({
    id,
    category: categoryId,
    name: values.name,
    tour_types: values.tour_types,
    duration: values.duration,
    duration_type: values.duration_type,
    description: values.description,

    adults: values.adults,
    adult_price: values.adult_price,
    adult_price_type: values.adult_price_type,

    children: values.children || 0,
    child_price: values.child_price || null,
    child_price_type: values.adult_price_type,

    infants: values.infants || 0,
    infant_price: values.infant_price || null,
    infant_price_type: values.adult_price_type,
    widgets_category: values.widgets_category,
    images: values.images
        ? values.images.fileList?.map(extractUrlFromFile)
        : [],
    extra_services: values.extra_services
        ? formatExtraServicesForSubmit(values.extra_services)
        : [],
    deleted_images: returnDeletedImages(initialImages, values.images?.fileList),
});

const GeneralDetails = ({
    form,
    id,
    isEdit,
    regStep,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const validations = useTranslatedValidationConfig(baseValidations);

    const hotelDetails = useSelector(selectors.hotelDetails);
    const generalInfo = useSelector(selectors.generalInfo);

    const initialImages = generalInfo?.images;

    // add mode
    const onAdd = useCallback((values) => {
        const data = formatDataToSubmit(id, 2, values, initialImages);
        const step = 1;
        const completedStep = 0;
        dispatch(actions.requestSubmitPackageGeneralDetails(
            data,
            step,
            completedStep,
        ));
    }, [dispatch, id, initialImages]);

    // edit mode
    const onEdit = useCallback((values) => {
        const data = formatDataToSubmit(id, 2, values, initialImages);
        const step = 1;
        const completedStep = 1;
        dispatch(actions.requestSubmitPackageGeneralDetails(
            data,
            step,
            completedStep,
        ));
    }, [dispatch, id, initialImages]);

    const errors = useSelector(selectors.errors);

    useApiValidationsForForm({ form, errors, formFields });

    const typeError = useSelector(selectors.packageTypeError);
    useEffect(() => {
        if (typeError !== null) {
            notification.error({
                description: t(typeError),
                duration: 0,
            });
        }
        dispatch(actions.clearPackageTypeError());
    }, [typeError]);

    const initialData = useMemo(() => {
        if (generalInfo) {
            return {
                name: generalInfo.name,
                tour_types: generalInfo.types
                    ? packageTypeFormat(generalInfo.types)
                    : [],
                duration: generalInfo.duration,
                duration_type: generalInfo.duration_type === 'Days' ? 2 : 1,
                description: generalInfo.description,
                adults: generalInfo.adults,
                adult_price: generalInfo.adult_price,
                adult_price_type: (generalInfo.adult_price_type === 'Per Person') ? 1 : 3,
                children: generalInfo.children,
                child_price: generalInfo.child_price,
                infants: generalInfo.infants,
                infant_price: generalInfo.infant_price,
                widgets_category: generalInfo
                    .widget_categories.map((category) => category.id),
                extra_services: eServicesFormat(generalInfo.extra_services),
                images: generalInfo.images.map((image) => (
                    {
                        id: image.id,
                        url: image.path,
                        thumbUrl: image.path,
                    }
                )),
            };
        } else {
            return {
                duration_type: 2,
                adult_price_type: 1,
            };
        }
    }, [generalInfo]);

    if (id && !initialData.name) {
        return <Loading />;
    }

    return (
        <Form
            form={form}
            id="packages-general-details-form"
            layout="vertical"
            requiredMark={false}
            onFinish={
                (isEdit && regStep > 1)
                || (!isEdit && id)
                || (isEdit && hotelDetails.length !== 0)
                    ? onEdit
                    : onAdd
            }
            initialValues={initialData || {}}
        >
            <HeaderWithHelp
                title="General Details"
                question={question}
                answer={answer}
            />

            <Divider />

            <Form.Item>
                <PackageDetails validations={validations} />
            </Form.Item>

            <DashedContainer title="Default Prices">
                <Form.Item>
                    <PackagePrices
                        validations={validations}
                        childNumber={initialData?.children}
                        infantNumber={initialData?.infants}
                    />
                </Form.Item>
            </DashedContainer>

            <DashedContainer title="Widget Category">
                <Form.Item>
                    <WidgetCategory />
                </Form.Item>
            </DashedContainer>

            <DashedContainer title="Extra Services">
                <TourExtraServices
                    name="extra_services"
                    validations={validations}
                    checkboxText="The price is inclusive to the package"
                />
            </DashedContainer>

            <CustomTextArea
                name="description"
                placeholder={t('Package Description')}
                rules={validations.description}
            />

            <Form.Item>
                <ImagesUpload />
            </Form.Item>
        </Form>
    );
};

export default GeneralDetails;
